import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { GlobalSettings } from './components/GlobalSettings';
import { CustomerDataTable } from './components/CustomerDataTable';
import { Customer } from './types/types';
import { apiService } from './services/api';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import Tooltip from '@mui/material/Tooltip';
import './App.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="tab-content">
          {children}
        </div>
      )}
    </div>
  );
}

function App() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const scrollPositionRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [customers]);

  const fetchData = async () => {
    scrollPositionRef.current = window.scrollY;
    
    setIsLoading(true); 
    setError(null);
    
    try {
      const data = await apiService.getExtendedConfig();
      if (data && data.customers) {
        setCustomers(data.customers);
      } else {
        setError('Invalid configuration data received');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load configuration.');
    } finally {
      setIsLoading(false); 
    }
  };

  useEffect(() => {
    const initialFetch = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const data = await apiService.getExtendedConfig();
          if (data && data.customers) { setCustomers(data.customers); }
          else { setError('Invalid configuration data received'); }
        } catch (error) { 
          console.error('Fetch error:', error);
          setError('Failed to load configuration.');
        } finally { setIsLoading(false); }
    };
    initialFetch();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
  };

  const handleDeleteCustomer = async (apikey: string) => {
    try {
      await apiService.removeCustomer(apikey);
      await fetchData();
      showNotification('Customer deleted successfully', 'success');
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Failed to delete customer', 'error');
    }
  };

  const handleAddCustomer = async (customer: Customer) => {
    try {
      await apiService.postConfig({ customers: [customer] });
      await fetchData();
      showNotification('Customer added successfully', 'success');
    } catch (error) {
      console.error('Add error:', error);
      showNotification('Failed to add customer', 'error');
      throw error;
    }
  };

  const handleEditCustomer = async (apikey: string, changes: Partial<Pick<Customer, 'readBatchSize' | 'readDelayMillis' | 'postURL'>>) => {
    try {
      await apiService.postConfig({
        customers: [{
          apikey,
          ...changes
        }]
      });
      await fetchData();
      showNotification('Customer settings updated successfully', 'success');
    } catch (error) {
      console.error('Edit error:', error);
      showNotification('Failed to update customer settings', 'error');
      throw error;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (error) {
    return (
      <div className="error-message">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => fetchData()} className="button primary">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-message">
        <h3>Loading Configuration...</h3>
        <p>Please wait while we fetch the settings...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>WebQueue Gateway Console</h1>
        <Tooltip title="Refresh Data" arrow>
          <button
            className="action-button refresh-app"
            onClick={() => fetchData()}
            disabled={isLoading}
          >
            <RefreshOutlinedIcon />
          </button>
        </Tooltip>
      </header>
      
      {notification && (
        <div className={`notification app-notification ${notification.type}`}> 
          {notification.message}
        </div>
      )}
      
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${tabValue === 0 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 0)}
            id="simple-tab-0"
            aria-controls="simple-tabpanel-0"
            aria-selected={tabValue === 0}
          >
            Configuration
          </button>
          <button
            className={`tab-button ${tabValue === 1 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 1)}
            id="simple-tab-1"
            aria-controls="simple-tabpanel-1"
            aria-selected={tabValue === 1}
          >
            Logs
          </button>
        </div>

        <TabPanel value={tabValue} index={0}>
          <div className="configuration-panel">
            <GlobalSettings />
            <CustomerDataTable
              customers={customers}
              onDelete={handleDeleteCustomer}
              onEdit={handleEditCustomer}
              onAdd={handleAddCustomer}
              onRefresh={fetchData}
              showNotification={showNotification}
            />
          </div>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <div className="logs-panel">
            <h2>Logs</h2>
            <p>Logs functionality coming soon...</p>
          </div>
        </TabPanel>
      </div>
    </div>
  );
}

export default App;